import { FC, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Col,
  List,
  Modal,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Switch,
  Typography
} from 'antd';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { APPOINTMENT_CREATOR } from 'constants/appointments';
import { translateBodies } from 'services/api/translations';

import { StyledLoading } from './styled';
import {
  TranslatableNote,
  replaceNotesWithArrayValues
} from 'utils/translation';
import { showRestError } from 'utils/showErrorMessage';

const { Title, Text, Link } = Typography;

const options = [
  { label: 'English', value: 'en' },
  { label: 'German', value: 'de' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Russian', value: 'ru' }
];

interface IProps {
  // display-only channel: legacy pushed translated notes via the Apollo
  // cache; now the owner (Notes) receives them here
  onTranslated?: (notes: TranslatableNote[]) => void;
}

const TranslationModal: FC<IProps> = ({ onTranslated }) => {
  // translations
  const { t, i18n } = useTranslation();
  // states
  const [showLanguages, setShowLanguages] = useState(false);
  const [language, setLanguage] = useState(i18n.language);
  const [translate, setTranslate] = useState(false);
  const [loading, setLoading] = useState(false);
  // context
  const { translation } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();

  const data = translation.params?.data as TranslatableNote[] | undefined;

  const notes = useMemo(
    () =>
      data?.filter(item => item?.sender === APPOINTMENT_CREATOR.patient) || [],
    [data]
  );

  const selectedLang = useMemo(
    () => options.find(e => language === e.value)?.label,
    [language]
  );

  const onOk = useCallback(() => {
    if (!translate) {
      toggleModal('translation', false);

      return;
    }

    const notesData = notes.map(note => note.note || '');

    setLoading(true);
    translateBodies(language, notesData)
      .then(body => {
        onTranslated?.(replaceNotesWithArrayValues(notes, body));
        toggleModal('translation', false);
      })
      .catch(err => showRestError(err))
      .finally(() => setLoading(false));
  }, [notes, language, toggleModal, translate, onTranslated]);

  const closeLanguages = () => {
    setShowLanguages(false);
  };

  const onChangeLang = ({ target: { value } }: RadioChangeEvent) => {
    setLanguage(value);
    setShowLanguages(false);
  };

  return (
    <Modal
      title={t('appointments_details.translation')}
      open={translation?.visible}
      closable={false}
      footer={[
        <Button
          key="ok"
          type="default"
          onClick={showLanguages ? closeLanguages : onOk}
        >
          {showLanguages ? t('common.cancel') : 'OK'}
        </Button>
      ]}
    >
      {loading ? (
        <StyledLoading>
          <LoadingOutlined style={{ fontSize: 50 }} />
        </StyledLoading>
      ) : showLanguages ? (
        <Radio.Group
          value={language}
          onChange={onChangeLang}
          style={{ width: '100%' }}
        >
          <List
            itemLayout="horizontal"
            dataSource={options}
            renderItem={item => (
              <List.Item>
                <List.Item.Meta title={t(`common:${item.label}`)} />
                <div>
                  <Radio value={item.value} />
                </div>
              </List.Item>
            )}
          />
        </Radio.Group>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row justify="space-between" align="top">
            <Col>
              <Title level={5} style={{ color: 'rgba(45, 44, 76, 0.85)' }}>
                {t('appointments_details.translate_conversation')}
              </Title>
              <Text type="secondary">{selectedLang}</Text>
            </Col>
            <Col>
              <Switch checked={translate} onChange={val => setTranslate(val)} />
            </Col>
          </Row>
          <Space direction="vertical" size={0}>
            <Text>{t('appointments_details.change_language')}</Text>
            <Link onClick={() => setShowLanguages(true)}>
              {t('appointments_details.translation_settings')}
            </Link>
          </Space>
        </Space>
      )}
    </Modal>
  );
};

export default TranslationModal;
